import { Box, Grid, Skeleton, Stack } from '@mui/material';
import React from 'react';
import i18n from '../../i18n';
import './scss/sub-function-settings-skeletons.component.scss';
import { KWA_CORE_SETTING_KEYS, KWA_RUNTIME_SETTING_KEYS } from './kwa-setting-constants';
import { MOP_CAROUSEL_SETTING_KEYS } from './mop-setting-constants';

/**
 * 子功能设置页共用的根类名：与各自实页最外层 BEM 一致，便于沿用布局样式。
 */
const rootSkeleton = (pageClass: string) => `${pageClass} ${pageClass}--skeleton rbp-sub-function-skeletons--root`;

/** 顶栏主标题骨架高度：与各实页 `Typography` `__page-title` 行高 32px 一致 */
const PAGE_TITLE_SKELETON_HEIGHT = 32;

type PageTitleSkeletonProps = {
    /** 标题条近似宽度（各页主标题长短不同） */
    width: number;
};

/**
 * 顶栏主标题占位：各子功能设置页保存按钮左侧标题条，高度与稿面/实页 h3～h4 行盒一致。
 */
function PageTitleSkeleton({ width }: PageTitleSkeletonProps) {
    return (
        <Skeleton
            className="rbp-sub-function-skeletons__page-title"
            variant="rounded"
            animation="wave"
            width={width}
            height={PAGE_TITLE_SKELETON_HEIGHT}
            sx={{ borderRadius: '8px' }}
        />
    );
}

/**
 * 顶栏「保存修改」占位：与 `SubFunctionProjectSettingSaveButton` 一致（min-height 48、圆角 8、宽度含 32 图标 + gap + 四字 20px 粗体 + padding）。
 */
function SaveButtonSkeleton() {
    return (
        <Skeleton
            className="rbp-sub-function-skeletons__save-btn"
            variant="rounded"
            animation="wave"
            width={184}
            height={48}
            sx={{ borderRadius: '8px' }}
        />
    );
}

/** 通用：左文右控件行骨架 */
function LabeledControlRowSkeleton() {
    return (
        <Box className="rbp-sub-function-skeletons__form-row" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Skeleton variant="rounded" width="60%" height={24} animation="wave" />
            <Skeleton variant="rounded" width={40} height={32} animation="wave" sx={{ borderRadius: 1 }} />
        </Box>
    );
}

/** KWA：顶栏、菜单多选区、双列 4+4 行。 */
export function KwaSettingPageSkeleton() {
    return (
        <Box className={rootSkeleton('kwa-setting-page')} aria-busy="true" aria-label={`${i18n.t('kwa_setting_page_heading')}, ${i18n.t('general_text_loading')}`}>
            <Box className="kwa-setting-page__surface">
                <Box className="kwa-setting-page__header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <PageTitleSkeleton width={280} />
                    <SaveButtonSkeleton />
                </Box>
                <Box className="kwa-setting-page__menu-card">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Skeleton width="40%" height={28} animation="wave" />
                        <Skeleton width={100} height={28} animation="wave" />
                    </Box>
                    <Skeleton variant="rounded" width="100%" height={88} animation="wave" sx={{ borderRadius: 2, mb: 1 }} />
                    <Skeleton variant="text" width="80%" height={20} />
                </Box>
                <Grid container spacing={3} className="kwa-setting-page__columns">
                    <Grid item xs={12} md={6}>
                        <Box className="kwa-setting-page__column">
                            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                <Skeleton width={180} height={28} animation="wave" />
                            </Box>
                            <Stack spacing={3}>
                                {KWA_CORE_SETTING_KEYS.map((k) => (
                                    <LabeledControlRowSkeleton key={k} />
                                ))}
                            </Stack>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box className="kwa-setting-page__column">
                            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                <Skeleton width={180} height={28} animation="wave" />
                            </Box>
                            <Stack spacing={3}>
                                {KWA_RUNTIME_SETTING_KEYS.map((k) => (
                                    <LabeledControlRowSkeleton key={k} />
                                ))}
                            </Stack>
                        </Box>
                    </Grid>
                </Grid>
                <Box className="kwa-setting-page__bottom-tip" sx={{ mt: 2 }}>
                    <Skeleton variant="rounded" width="100%" height={72} animation="wave" />
                </Box>
            </Box>
        </Box>
    );
}

const RWA_MENU_SKELETON_ROWS = 6;

/** RWA：左表 + 右表单项。 */
export function RwaSettingPageSkeleton() {
    return (
        <Box className={rootSkeleton('rwa-setting-page')} aria-busy="true" aria-label={`${i18n.t('rwa_setting_page_heading')}, ${i18n.t('general_text_loading')}`}>
            <Box className="rwa-setting-page__surface">
                <Box className="rwa-setting-page__header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <PageTitleSkeleton width={300} />
                    <SaveButtonSkeleton />
                </Box>
                <Grid container spacing={3} className="rwa-setting-page__columns">
                    <Grid item xs={12} md={6}>
                        <Box className="rwa-setting-page__left-card">
                            <Skeleton width="60%" height={28} sx={{ mb: 1 }} />
                    <LabeledControlRowSkeleton />
                    <Box sx={{ my: 1.5 }} />
                    {Array.from({ length: RWA_MENU_SKELETON_ROWS }, (_, i) => (
                        <LabeledControlRowSkeleton key={i} />
                    ))}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box className="rwa-setting-page__right-card">
                            <Skeleton width="50%" height={28} sx={{ mb: 2 }} />
                            {[0, 1, 2, 3].map((i) => (
                                <Box key={i} sx={{ mb: 2 }}>
                                    <Skeleton width="70%" height={20} sx={{ mb: 0.5 }} />
                                    <Skeleton variant="rounded" height={40} width="100%" />
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}

/** MOP：开关 + 五卡轮播位。 */
export function MopSettingPageSkeleton() {
    return (
        <Box className={rootSkeleton('mop-setting-page')} aria-busy="true" aria-label={`${i18n.t('mop_setting_page_heading')}, ${i18n.t('general_text_loading')}`}>
            <Box className="mop-setting-page__surface">
                <Box
                    className="mop-setting-page__header"
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 48 }}
                >
                    <PageTitleSkeleton width={240} />
                    <SaveButtonSkeleton />
                </Box>
                <Box className="mop-setting-page__card">
                    <Skeleton width="30%" height={24} sx={{ mb: 2 }} />
                    <LabeledControlRowSkeleton />
                    <Box sx={{ mt: 3 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Skeleton width="35%" height={24} />
                        <Skeleton width={80} height={24} />
                    </Box>
                    <Box className="mop-setting-page__carousel" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {MOP_CAROUSEL_SETTING_KEYS.map((k) => (
                            <Skeleton
                                key={k}
                                variant="rounded"
                                width={256}
                                height={160}
                                animation="wave"
                                sx={{ borderRadius: 1 }}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

/**
 * COP：与实页一致——灰底 surface、白卡、区块标题 + 两行 `field-row`（开关行 / 240×56 扫码下拉行，见 `cop-setting.page.scss`）。
 */
export function CopSettingPageSkeleton() {
    return (
        <Box className={rootSkeleton('cop-setting-page')} aria-busy="true" aria-label={`${i18n.t('cop_setting_page_heading')}, ${i18n.t('general_text_loading')}`}>
            <Box className="cop-setting-page__surface">
                <Box className="cop-setting-page__header" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <PageTitleSkeleton width={280} />
                    <SaveButtonSkeleton />
                </Box>
                <Box className="cop-setting-page__card">
                    <Skeleton
                        variant="text"
                        animation="wave"
                        width="38%"
                        height={24}
                        className="cop-setting-page__section-title"
                        sx={{ display: 'block', boxSizing: 'content-box' }}
                    />
                    <Stack spacing={3} className="cop-setting-page__card-body">
                        {/** 稿 1897:22538：左文案 + 右开关 */}
                        <Box className="cop-setting-page__field-row">
                            <Stack spacing={0.5} className="cop-setting-page__field-label">
                                <Skeleton width={200} height={24} animation="wave" />
                                <Skeleton width={240} height={20} animation="wave" />
                            </Stack>
                            <Box className="cop-setting-page__field-control">
                                <Skeleton variant="rounded" width={40} height={24} animation="wave" sx={{ borderRadius: 999 }} />
                            </Box>
                        </Box>
                        {/** 稿 1897:22629：主副文案 max 280 + 右列固定 240 下拉位；左侧须给具体宽度，否则 flex 下子项 width:100% 参照宽为 0 */}
                        <Box className="cop-setting-page__field-row cop-setting-page__field-row--qr">
                            <Stack
                                spacing={1}
                                className="cop-setting-page__field-label cop-setting-page__field-label--qr"
                                sx={{ width: 280, maxWidth: '100%', minWidth: 0, flex: '0 0 auto' }}
                            >
                                <Skeleton width="100%" height={24} animation="wave" />
                                <Skeleton width="100%" height={20} animation="wave" />
                            </Stack>
                            <Box className="cop-setting-page__field-control cop-setting-page__field-control--select">
                                <Skeleton variant="rounded" width="100%" height={56} animation="wave" sx={{ borderRadius: '16px', maxWidth: 240 }} />
                            </Box>
                        </Box>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
}

/** 与 `buildInitialBasicRows` 行数一致（7 行：开关 + 数字项） */
const QOP_BASIC_SETTING_ROWS = 7;
/** 模板位：8 套内置 + 1 上传位，实页 `qop-setting-page__template-strip` 横向换行 */
const QOP_TEMPLATE_STRIP_PLACEHOLDERS = 9;
/** 右列桌台列表示意行数（`__table-list` 内可滚动，不必与接口桌台数一致） */
const QOP_TABLE_LIST_SKELETON_ROWS = 6;

/**
 * 左列单条：左文案 + 右开关或数字位（`qop-setting-page__row` 与稿面约 48px 行高接近）。
 * @param variant - 右侧占位形态：开关注脚块 / 加长的数字/胶囊位
 */
function QopLeftBasicRowSkeleton({ variant = 'control' }: { variant?: 'control' | 'metric' }) {
    return (
        <Box className="qop-setting-page__row" sx={{ mb: 0, pointerEvents: 'none' }}>
            <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
                <Stack spacing={0.5}>
                    <Skeleton animation="wave" width="72%" height={20} />
                    <Skeleton animation="wave" width="90%" height={16} />
                </Stack>
            </Box>
            <Box sx={{ flexShrink: 0, alignSelf: 'center' }}>
                {variant === 'metric' ? (
                    <Skeleton animation="wave" variant="rounded" width={200} height={40} sx={{ borderRadius: '16px' }} />
                ) : (
                    <Skeleton animation="wave" variant="rounded" width={40} height={32} sx={{ borderRadius: 1 }} />
                )}
            </Box>
        </Box>
    );
}

/**
 * 右列桌台列表单行：左图标+文案、右三枚操作位（与 `qop-setting-page__table-row` 同款布局）。
 */
function QopTableRowSkeleton() {
    return (
        <Box className="qop-setting-page__table-row" aria-hidden>
            <Stack direction="row" spacing={2} alignItems="center" className="qop-setting-page__table-row-main" sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="circular" width={32} height={32} animation="wave" />
                <Skeleton animation="wave" width={120} height={20} />
                <Skeleton animation="wave" width="28%" height={24} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
                <Skeleton animation="wave" variant="rounded" width={32} height={32} />
                <Skeleton animation="wave" variant="rounded" width={32} height={32} />
                <Skeleton animation="wave" variant="rounded" width={32} height={32} />
            </Stack>
        </Box>
    );
}

/**
 * QOP：对齐 `qop-setting.page`——左列基础设置→清空密码→`section-block--before-grid`+`qr-network-fields`(56px 输入)；右列 `right-rail-root`/`right-rail`→公网块占位→扫码语言→外卖同行→`table-rail`(桌台标题+右对齐工具 chip+列表)。
 */
export function QopSettingPageSkeleton() {
    return (
        <Box className={rootSkeleton('qop-setting-page')} aria-busy="true" aria-label={`${i18n.t('qop_setting_page_heading')}, ${i18n.t('general_text_loading')}`}>
            <Box className="qop-setting-page__surface">
                <Box
                    className="qop-setting-page__header"
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 48 }}
                >
                    <PageTitleSkeleton width={200} />
                    <SaveButtonSkeleton />
                </Box>
                <Grid container spacing={2} className="qop-setting-page__columns">
                    {/** 左列白卡：与实页同序——基础区标题 → 7 行 → 清除密码 → 二维码小标题+hint → 2×2 字段 → 模板工具条+缩略图带 */}
                    <Grid item xs={12} lg={6} sx={{ display: 'flex', minWidth: 0 }}>
                        <Box className="qop-setting-page__card qop-setting-page__card--column" sx={{ width: '100%', minWidth: 0 }}>
                            <Skeleton
                                className="qop-setting-page__section-title"
                                width="40%"
                                height={24}
                                animation="wave"
                                sx={{ mb: 2, display: 'block' }}
                            />
                            <Stack spacing={1} sx={{ width: '100%' }}>
                                {Array.from({ length: QOP_BASIC_SETTING_ROWS }, (_, i) => (
                                    <QopLeftBasicRowSkeleton key={i} variant={i === 3 || i === 6 ? 'metric' : 'control'} />
                                ))}
                            </Stack>
                            <Box className="qop-setting-page__clear-password" sx={{ pointerEvents: 'none' }}>
                                <Skeleton variant="rounded" width={280} height={52} animation="wave" sx={{ borderRadius: '8px' }} />
                            </Box>
                            {/** 与实页 `section-block` + `section-block--before-grid` 一致（subtitle2 + 可选辅助说明位） */}
                            <Box
                                className="qop-setting-page__section-block qop-setting-page__section-block--before-grid"
                                sx={{ pointerEvents: 'none' }}
                            >
                                <Skeleton width="48%" height={20} animation="wave" />
                                <Skeleton width="100%" height={16} animation="wave" sx={{ mt: 0.5 }} />
                            </Box>
                            {/** 表面输入区高度与 `CommonInput` surface 56px 一致 */}
                            <Stack spacing={2} className="qop-setting-page__qr-network-fields" sx={{ width: '100%', px: '8px', boxSizing: 'border-box' }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    {[0, 1].map((f) => (
                                        <Box key={f} sx={{ flex: 1, minWidth: 0 }} className="qop-setting-page__field">
                                            <Skeleton className="qop-setting-page__field-label" width="50%" height={20} animation="wave" sx={{ mb: 1, display: 'block' }} />
                                            <Skeleton variant="rounded" width="100%" height={56} animation="wave" sx={{ borderRadius: 1 }} />
                                        </Box>
                                    ))}
                                </Stack>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    {[2, 3].map((f) => (
                                        <Box key={f} sx={{ flex: 1, minWidth: 0 }} className="qop-setting-page__field">
                                            <Skeleton className="qop-setting-page__field-label" width="50%" height={20} animation="wave" sx={{ mb: 1, display: 'block' }} />
                                            <Skeleton variant="rounded" width="100%" height={56} animation="wave" sx={{ borderRadius: 1 }} />
                                        </Box>
                                    ))}
                                </Stack>
                            </Stack>
                            <Box className="qop-setting-page__template-toolbar" sx={{ pointerEvents: 'none' }}>
                                <Skeleton width="36%" height={24} animation="wave" />
                                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Skeleton variant="circular" width={20} height={20} />
                                        <Skeleton width={64} height={20} />
                                        <Skeleton variant="circular" width={20} height={20} />
                                        <Skeleton width={64} height={20} />
                                    </Stack>
                                    <Skeleton variant="rounded" width={140} height={36} animation="wave" sx={{ borderRadius: 1 }} />
                                </Stack>
                            </Box>
                            <Box className="qop-setting-page__template-strip" sx={{ flexWrap: 'wrap', display: 'flex' }}>
                                {Array.from({ length: QOP_TEMPLATE_STRIP_PLACEHOLDERS }, (_, t) => (
                                    <Skeleton
                                        key={t}
                                        variant="rounded"
                                        width={100}
                                        height={160}
                                        animation="wave"
                                        sx={{ borderRadius: '12px', flexShrink: 0 }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                    {/** 右列：与实页 `right-rail-root` + `right-rail` Stack 间距 24；二维码说明 → 公网区占位 → 扫码/语言 → 外卖同行 → `table-rail`（桌台标题 + toolbar-panel + 列表） */}
                    <Grid item xs={12} lg={6} sx={{ display: 'flex', minWidth: 0, alignItems: 'stretch' }}>
                        <Box
                            className="qop-setting-page__card qop-setting-page__card--column qop-setting-page__card--right-rail-root"
                            sx={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column' }}
                        >
                            <Stack spacing={3} className="qop-setting-page__right-rail" sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                                <Box className="qop-setting-page__section-block" sx={{ pointerEvents: 'none' }}>
                                    <Skeleton width="40%" height={20} animation="wave" />
                                    <Skeleton width="100%" height={16} animation="wave" sx={{ mt: 0.5 }} />
                                </Box>
                                <Box sx={{ pointerEvents: 'none' }}>
                                    <Box className="qop-setting-page__section-block">
                                        <Skeleton width="42%" height={20} animation="wave" />
                                        <Skeleton width="100%" height={16} animation="wave" sx={{ mt: 0.5 }} />
                                    </Box>
                                    <Stack direction="row" spacing={2} alignItems="flex-end" className="qop-setting-page__public-url-row">
                                        <Skeleton variant="rounded" height={56} animation="wave" sx={{ flex: 1, minWidth: 0, borderRadius: 1 }} />
                                        <Skeleton variant="rounded" width={140} height={56} animation="wave" sx={{ borderRadius: 1, flexShrink: 0 }} />
                                    </Stack>
                                </Box>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={3}
                                    className="qop-setting-page__scan-lang-row"
                                    sx={{ width: '100%', px: '8px', boxSizing: 'border-box' }}
                                >
                                    {[0, 1].map((c) => (
                                        <Box key={c} sx={{ flex: 1, minWidth: 0 }} className="qop-setting-page__field">
                                            <Skeleton className="qop-setting-page__field-label" width="45%" height={20} animation="wave" sx={{ mb: 1, display: 'block' }} />
                                            <Skeleton variant="rounded" width="100%" height={56} animation="wave" sx={{ borderRadius: 1 }} />
                                        </Box>
                                    ))}
                                </Stack>
                                <Stack
                                    direction="row"
                                    alignItems="flex-start"
                                    justifyContent="space-between"
                                    spacing={2}
                                    className="qop-setting-page__takeaway-row"
                                    flexWrap="wrap"
                                    sx={{ width: '100%', px: '8px', boxSizing: 'border-box', mb: 0 }}
                                >
                                    <Skeleton width="38%" height={20} animation="wave" sx={{ flexShrink: 0 }} />
                                    <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
                                        <Skeleton variant="rounded" width={120} height={40} animation="wave" sx={{ borderRadius: 1 }} />
                                        <Skeleton variant="rounded" width={120} height={40} animation="wave" sx={{ borderRadius: 1 }} />
                                    </Stack>
                                </Stack>
                                <Box
                                    className="qop-setting-page__table-rail"
                                    sx={{
                                        flex: 1,
                                        minHeight: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        width: '100%',
                                    }}
                                >
                                    <Box className="qop-setting-page__section-block" sx={{ pointerEvents: 'none' }}>
                                        <Skeleton width="44%" height={20} animation="wave" />
                                        <Skeleton width="100%" height={16} animation="wave" sx={{ mt: 0.5 }} />
                                    </Box>
                                    <Stack spacing={1} className="qop-setting-page__table-toolbar-panel" sx={{ width: '100%' }}>
                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            flexWrap="wrap"
                                            justifyContent="flex-end"
                                            className="qop-setting-page__table-toolbar"
                                            sx={{ width: '100%' }}
                                        >
                                            <Skeleton variant="rounded" width={120} height={40} animation="wave" sx={{ borderRadius: 1 }} />
                                            <Skeleton variant="rounded" width={200} height={40} animation="wave" sx={{ borderRadius: 1 }} />
                                            <Skeleton variant="rounded" width={220} height={40} animation="wave" sx={{ borderRadius: 1 }} />
                                        </Stack>
                                    </Stack>
                                    <Box
                                        className="qop-setting-page__table-list"
                                        sx={{
                                            flex: '1 1 auto',
                                            minHeight: 200,
                                            maxHeight: 1000,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        {Array.from({ length: QOP_TABLE_LIST_SKELETON_ROWS }, (_, r) => (
                                            <QopTableRowSkeleton key={r} />
                                        ))}
                                    </Box>
                                </Box>
                            </Stack>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}

const DIS_TABLE_SKELETON_ROWS = 4;

/**
 * DIS：与 `dis-setting.page` 一致——发票设置 **`invoice-paired-grid`** 与实页成对顺序一致；发票打印叠 **`gray-inputs`**；税务/GYB **`table-head` + `datatable`**。
 */
export function DisSettingPageSkeleton() {
    return (
        <Box className={rootSkeleton('dis-setting-page')} aria-busy="true" aria-label={`${i18n.t('dis_setting_page_heading')}, ${i18n.t('general_text_loading')}`}>
            <Box className="dis-setting-page__surface">
                <Box className="dis-setting-page__header" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <PageTitleSkeleton width={300} />
                    <SaveButtonSkeleton />
                </Box>

                {/** 发票打印位置：标题 + 左多选区 + 右三字段 */}
                <Box className="dis-setting-page__card dis-setting-page__card--section">
                    <Skeleton
                        className="dis-setting-page__section-title"
                        variant="text"
                        width="42%"
                        height={24}
                        animation="wave"
                        sx={{ display: 'block', mb: 2, boxSizing: 'content-box' }}
                    />
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems="flex-end"
                        className="dis-setting-page__print-row dis-setting-page__gray-inputs"
                        sx={{ width: '100%' }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                            <Skeleton width={160} height={20} animation="wave" sx={{ mb: 1 }} />
                            <Skeleton variant="rounded" width="100%" height={40} animation="wave" />
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-end" sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                            <Skeleton variant="rounded" height={56} animation="wave" sx={{ flex: 1, minWidth: 0, borderRadius: 1 }} />
                            <Skeleton variant="rounded" height={56} animation="wave" sx={{ flex: 1, minWidth: 0, borderRadius: 1 }} />
                            <Skeleton variant="rounded" height={56} animation="wave" sx={{ flex: 1, minWidth: 0, borderRadius: 1 }} />
                        </Stack>
                    </Stack>
                </Box>

                {/** 发票设置：标题行 + 双列表单项 */}
                <Box className="dis-setting-page__card dis-setting-page__card--section dis-setting-page__invoice-card">
                    <Box className="dis-setting-page__invoice-title-row">
                        <Skeleton width={220} height={24} animation="wave" />
                        <Skeleton width={100} height={32} animation="wave" />
                    </Box>
                    <Box className="dis-setting-page__invoice-body dis-setting-page__gray-inputs">
                        <Box className="dis-setting-page__invoice-paired-grid">
                            <Box className="dis-setting-page__invoice-pair-cell">
                                <Box
                                    className="dis-setting-page__invoice-field-row dis-setting-page__invoice-field-row--stacked dis-setting-page__invoice-field-row--radios"
                                    sx={{ width: '100%' }}
                                >
                                    <Stack spacing={0.5} className="dis-setting-page__invoice-field-label" sx={{ width: '100%' }}>
                                        <Skeleton width={200} height={20} animation="wave" />
                                        <Skeleton width="80%" height={16} animation="wave" />
                                    </Stack>
                                    <Box className="dis-setting-page__invoice-field-control" sx={{ width: '100%' }}>
                                        <Skeleton variant="rounded" width="100%" height={72} animation="wave" />
                                    </Box>
                                </Box>
                            </Box>
                            <Box className="dis-setting-page__invoice-pair-cell">
                                <Box className="dis-setting-page__invoice-field-row dis-setting-page__invoice-field-row--stacked" sx={{ width: '100%' }}>
                                    <Stack spacing={0.5} className="dis-setting-page__invoice-field-label" sx={{ width: '100%' }}>
                                        <Skeleton width="100%" height={20} animation="wave" />
                                    </Stack>
                                    <Box className="dis-setting-page__invoice-field-control" sx={{ width: '100%' }}>
                                        <Skeleton variant="rounded" width="100%" height={40} animation="wave" />
                                    </Box>
                                </Box>
                            </Box>
                            <Box className="dis-setting-page__invoice-pair-cell">
                                <Box className="dis-setting-page__invoice-field-row" sx={{ width: '100%' }}>
                                    <Stack spacing={0.5} className="dis-setting-page__invoice-field-label" sx={{ width: 280, maxWidth: '40%', minWidth: 0, flexShrink: 0 }}>
                                        <Skeleton width="100%" height={20} animation="wave" />
                                    </Stack>
                                    <Box className="dis-setting-page__invoice-field-control dis-setting-page__invoice-field-control--switch" sx={{ flex: 1, minWidth: 0 }}>
                                        <Skeleton variant="rounded" width={44} height={28} animation="wave" sx={{ borderRadius: 999, ml: 'auto' }} />
                                    </Box>
                                </Box>
                            </Box>
                            <Box className="dis-setting-page__invoice-pair-cell">
                                <Box className="dis-setting-page__invoice-field-row" sx={{ width: '100%' }}>
                                    <Stack spacing={0.5} className="dis-setting-page__invoice-field-label" sx={{ width: 280, maxWidth: '40%', minWidth: 0, flexShrink: 0 }}>
                                        <Skeleton width="100%" height={20} animation="wave" />
                                        <Skeleton width="72%" height={16} animation="wave" />
                                    </Stack>
                                    <Box className="dis-setting-page__invoice-field-control" sx={{ flex: 1, minWidth: 0 }}>
                                        <Skeleton variant="rounded" width="100%" height={40} animation="wave" />
                                    </Box>
                                </Box>
                            </Box>
                            <Box className="dis-setting-page__invoice-pair-cell">
                                <Box className="dis-setting-page__invoice-field-row" sx={{ width: '100%' }}>
                                    <Stack spacing={0.5} className="dis-setting-page__invoice-field-label" sx={{ width: 280, maxWidth: '40%', minWidth: 0, flexShrink: 0 }}>
                                        <Skeleton width="100%" height={20} animation="wave" />
                                        <Skeleton width="65%" height={16} animation="wave" />
                                    </Stack>
                                    <Box className="dis-setting-page__invoice-field-control" sx={{ flex: 1, minWidth: 0 }}>
                                        <Skeleton variant="rounded" width="100%" height={40} animation="wave" />
                                    </Box>
                                </Box>
                            </Box>
                            <Box className="dis-setting-page__invoice-pair-cell">
                                <Box className="dis-setting-page__invoice-field-row" sx={{ width: '100%' }}>
                                    <Stack spacing={0.5} className="dis-setting-page__invoice-field-label" sx={{ width: 280, maxWidth: '40%', minWidth: 0, flexShrink: 0 }}>
                                        <Skeleton width="100%" height={20} animation="wave" />
                                        <Skeleton width="70%" height={16} animation="wave" />
                                    </Stack>
                                    <Box className="dis-setting-page__invoice-field-control" sx={{ flex: 1, minWidth: 0 }}>
                                        <Skeleton variant="rounded" width="100%" height={40} animation="wave" />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/** 餐厅税务信息表头 + 表行占位 */}
                <Box className="dis-setting-page__card dis-setting-page__card--section">
                    <Box className="dis-setting-page__table-head">
                        <Box sx={{ minWidth: 0 }}>
                            <Skeleton width={200} height={24} animation="wave" sx={{ mb: 0.5 }} />
                            <Skeleton height={18} animation="wave" sx={{ maxWidth: 360, width: '100%' }} />
                        </Box>
                        <Skeleton width={120} height={36} animation="wave" variant="rounded" sx={{ borderRadius: 1, flexShrink: 0 }} />
                    </Box>
                    <Box className="dis-setting-page__datatable" sx={{ width: '100%' }}>
                        <Stack spacing={1} sx={{ width: '100%' }}>
                            {Array.from({ length: DIS_TABLE_SKELETON_ROWS }, (_, i) => (
                                <Skeleton key={`dis-tax-${i}`} variant="rounded" width="100%" height={44} animation="wave" />
                            ))}
                        </Stack>
                    </Box>
                </Box>

                {/** GYB 设备：同结构表头 + `datatable` 占位（实页非西班牙才展示，骨架统一占位不区分国家） */}
                <Box className="dis-setting-page__card dis-setting-page__card--section">
                    <Box className="dis-setting-page__table-head">
                        <Box sx={{ minWidth: 0 }}>
                            <Skeleton width={180} height={24} animation="wave" sx={{ mb: 0.5 }} />
                            <Skeleton height={18} animation="wave" sx={{ maxWidth: 360, width: '100%' }} />
                        </Box>
                        <Skeleton width={100} height={36} animation="wave" variant="rounded" sx={{ borderRadius: 1, flexShrink: 0 }} />
                    </Box>
                    <Box className="dis-setting-page__datatable" sx={{ width: '100%' }}>
                        <Stack spacing={1} sx={{ width: '100%' }}>
                            {Array.from({ length: DIS_TABLE_SKELETON_ROWS }, (_, i) => (
                                <Skeleton key={`dis-gyb-${i}`} variant="rounded" width="100%" height={44} animation="wave" />
                            ))}
                        </Stack>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

const CALL_SVC_TABLE_ROW_COUNT = 5;

/**
 * 呼叫服务：与 `call-service.page` 一致——灰 surface、仅页头+列表区+底部「新增」、表头透底/表体 80 白行/行距 16（`call-service.page.scss`）。
 */
export function CallServicePageSkeleton() {
    return (
        <Box className={rootSkeleton('call-service-page')} aria-busy="true" aria-label={`${i18n.t('call_service_setting_page_heading')}, ${i18n.t('general_text_loading')}`}>
            <Box className="call-service-page__surface">
                {/** 与实页相同：左列 flex:1 放 h4 标题 + 页级辅助说明位 */}
                <Box className="call-service-page__header">
                    <Box sx={{ flex: '1 1 auto', minWidth: 0, width: '100%' }}>
                        <Skeleton
                            variant="text"
                            animation="wave"
                            className="call-service-page__page-title"
                            sx={{ display: 'block', lineHeight: '32px', height: 32, maxWidth: 400, width: { xs: '100%', sm: 320 } }}
                        />
                        <Skeleton height={20} width="100%" animation="wave" sx={{ maxWidth: 640, mt: 0.5 }} />
                    </Box>
                </Box>

                <Box className="call-service-page__table-card" sx={{ width: '100%' }}>
                    <Box className="call-service-page__datatable" sx={{ width: '100%' }}>
                        {/** 表头行：全选 + 6 列，字色在实页为 theme 紫，这里用淡紫条占位 */}
                        <Box
                            aria-hidden
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '48px repeat(6, minmax(0, 1fr))',
                                alignItems: 'center',
                                minHeight: 40,
                                mb: 0.5,
                                boxSizing: 'border-box',
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Skeleton variant="rounded" width={22} height={22} animation="wave" />
                            </Box>
                            {Array.from({ length: 6 }, (_, c) => (
                                <Box key={c} sx={{ px: 0.5, pl: 1, minWidth: 0 }}>
                                    <Skeleton
                                        width={c === 0 ? 36 : 28}
                                        height={20}
                                        animation="wave"
                                        sx={{
                                            backgroundColor: 'var(--fill-primary-200, rgba(116, 104, 240, 0.2))',
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>

                        {/** 行间距 16、每行 80 高白底，贴合 tbody tr 高 80 */}
                        <Stack spacing={2} sx={{ width: '100%' }}>
                            {Array.from({ length: CALL_SVC_TABLE_ROW_COUNT }, (_, r) => (
                                <Box
                                    key={r}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '48px repeat(6, minmax(0, 1fr))',
                                        alignItems: 'center',
                                        minHeight: 80,
                                        maxHeight: 80,
                                        boxSizing: 'border-box',
                                        borderRadius: '16px',
                                        backgroundColor: 'var(--fill-neutral-100, #fff)',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 80, py: 0, px: 0.5 }}>
                                        <Skeleton variant="rounded" width={22} height={22} animation="wave" />
                                    </Box>
                                    {Array.from({ length: 6 }, (_, c) => (
                                        <Box
                                            key={c}
                                            component="div"
                                            sx={{ px: 1, minWidth: 0, maxHeight: 80, display: 'flex', alignItems: 'center' }}
                                        >
                                            <Skeleton width="100%" height={24} animation="wave" />
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                        </Stack>
                    </Box>

                    {/** 与 `call-service-page__add-row-btn` 同高同宽，底部整行可点区占位 */}
                    <Box className="call-service-page__table-footer" aria-hidden>
                        <Box className="call-service-page__add-row-btn" sx={{ pointerEvents: 'none' }}>
                            <Skeleton
                                height={32}
                                width={220}
                                animation="wave"
                                sx={{
                                    maxWidth: '100%',
                                    bgcolor: 'var(--fill-primary-200, rgba(116, 104, 240, 0.2))',
                                }}
                            />
                            <Skeleton
                                className="call-service-page__add-row-icon"
                                width={32}
                                height={32}
                                animation="wave"
                                variant="circular"
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
